/**
 * The main thread's way to analyse a second text (DESIGN UD-22): a worker of its own, apart from the one that holds the open
 * document, so the document's find and exports are not queued behind a long analysis and cancelling this one (ending its
 * worker) leaves the document untouched. The worker answers a `summarise` request with the summary only; nothing is stored.
 */
import type { AnalysisSummary } from '../../shared/summary.ts';
import { WorkerClient } from '../worker-client.ts';

let own: WorkerClient | null = null;

/**
 * Runs the analysis pipeline on `text` and resolves with its summary. `onProgress` gets the overall fraction (0 to 1) by the
 * stage weights. Aborting `signal` ends the worker and rejects with an `AbortError`. With `opts.working` the worker first
 * takes `text` to the bench's working text (`workingText`).
 */
export function summariseInWorker(
	text: string,
	onProgress?: (fraction: number) => void,
	signal?: AbortSignal,
	opts?: { working?: boolean }
): Promise<AnalysisSummary> {
	if (!own) own = new WorkerClient();
	const client = own;
	const id = client.id();
	return new Promise<AnalysisSummary>((resolve, reject) => {
		let off: () => void = () => undefined;
		const stop = () => {
			off();
			signal?.removeEventListener('abort', onAbort);
		};
		const onAbort = () => {
			stop();
			// the analysis is synchronous CPU work inside the engine: only ending the worker stops it
			client.restart();
			reject(Object.assign(new Error('The measurement was cancelled.'), { name: 'AbortError' }));
		};
		if (signal?.aborted) return onAbort();
		signal?.addEventListener('abort', onAbort);
		off = client.subscribe((m) => {
			if (m.id !== id) return;
			if (m.type === 'progress') onProgress?.(Math.min(1, m.pct / 100));
			else if (m.type === 'summary') {
				stop();
				resolve(m.summary);
			} else if (m.type === 'error') {
				stop();
				reject(new Error(m.message));
			} else if (m.type === 'cancelled') {
				stop();
				reject(Object.assign(new Error('The measurement was cancelled.'), { name: 'AbortError' }));
			}
		});
		client.onCrash = () => {
			stop();
			reject(new Error('The measuring worker stopped unexpectedly.'));
		};
		client.send({ type: 'summarise', id, text, ...(opts?.working ? { working: true } : {}) });
	});
}
